import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash, UsersThree } from '@phosphor-icons/react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { DatePickerPopover } from '@/components/ui/date-picker-popover';
import { TimePickerPopover } from '@/components/ui/time-picker-popover';
import { TeamPicker } from '@/components/schedule/TeamPicker';
import {
  MatchDetailsSection,
  MatchResultSection,
} from '@/components/schedule/MatchDetailsSection';
import { useConfirm } from '@/components/ConfirmDialog';
import { useToast } from '@/components/Toast';
import {
  ACTIVITY_TYPES,
  getActivity,
  type Activity,
  type ActivityInput,
  type ActivityType,
} from '@/api/activities';
import { getTeam } from '@/api/teams';
import { listTeamCategories } from '@/api/teamCategories';
import { listLocations } from '@/api/locations';
import {
  closeCalling,
  createCalling,
  listInvitationsForActivity,
  respondCalling,
  setCallingExclusions,
  type ActivityInvitation,
  type CallingResponseStatus,
} from '@/api/callings';
import {
  emptyMatchDetails,
  getFixture,
  matchDetailsFromFixture,
  type MatchDetailsValue,
} from '@/api/fixtures';
import { CALLING_LEAD_VALUES, leadFromSelect, leadOptionKey, leadToSelect } from '@/lib/callingLead';
import type { TeamLite } from '@/hooks/useAsyncTeamSearch';

// ActivitySheet — right-side panel that doubles as the activity editor.
// All fields are live-editable; Save commits via the parent-owned update
// mutation. The callings roster lives in a collapsed accordion at the
// bottom, with RSVP counts surfaced in the trigger row so a glance at
// the closed accordion answers "how many people have replied".

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  activityId: string | null;
  onSubmit: (id: string, input: ActivityInput, match?: MatchDetailsValue | null) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
  submitting: boolean;
  deleting: boolean;
  submitError: string | null;
}

export function ActivitySheet({
  open,
  onOpenChange,
  slug,
  activityId,
  onSubmit,
  onDelete,
  submitting,
  deleting,
  submitError,
}: Props) {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const confirm = useConfirm();
  const contentRef = useRef<HTMLDivElement>(null);

  const activityQuery = useQuery({
    queryKey: ['activity', slug, activityId],
    queryFn: () => getActivity(token, slug, activityId ?? ''),
    enabled: open && !!slug && !!activityId,
  });

  const activity = activityQuery.data ?? null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        ref={contentRef}
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
      >
        <SheetTitle className="sr-only">
          {activity ? activity.title : t('activityDetail.notFound')}
        </SheetTitle>

        {activityQuery.isLoading ? (
          <p className="px-6 pt-12 text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : !activity ? (
          <p className="px-6 pt-12 text-sm text-destructive">{t('activityDetail.notFound')}</p>
        ) : (
          <ActivityEditor
            key={activity.id}
            slug={slug}
            activity={activity}
            portalContainer={contentRef.current}
            onSubmit={onSubmit}
            onDelete={async (id) => {
              if (
                await confirm({
                  title: t('schedule.delete.title'),
                  body: t('schedule.delete.body'),
                  confirmLabel: t('schedule.delete.cta'),
                  tone: 'destructive',
                })
              ) {
                await onDelete(id);
              }
            }}
            submitting={submitting}
            deleting={deleting}
            submitError={submitError}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

interface EditorProps {
  slug: string;
  activity: Activity;
  portalContainer: HTMLElement | null;
  onSubmit: (id: string, input: ActivityInput, match?: MatchDetailsValue | null) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
  submitting: boolean;
  deleting: boolean;
  submitError: string | null;
}

function ActivityEditor({
  slug,
  activity,
  portalContainer,
  onSubmit,
  onDelete,
  submitting,
  deleting,
  submitError,
}: EditorProps) {
  const { t, i18n } = useTranslation('common');

  const [title, setTitle] = useState(activity.title);
  const [description, setDescription] = useState(activity.description ?? '');
  const [activityType, setActivityType] = useState<ActivityType>(activity.activity_type);
  const [locationId, setLocationId] = useState<string>(activity.location_id ?? '');
  const [zoneId, setZoneId] = useState<string>(activity.zone_id ?? '');
  const [startsAt, setStartsAt] = useState<string>(toLocalInput(activity.starts_at));
  const [endsAt, setEndsAt] = useState<string>(toLocalInput(activity.ends_at));
  // The pickers edit the two halves; startsAt / endsAt stay the combined local
  // 'YYYY-MM-DDTHH:MM' string that validation and submit already speak.
  const { date: startDate, time: startTime } = splitLocal(startsAt);
  const { date: endDate, time: endTime } = splitLocal(endsAt);
  const [team, setTeam] = useState<TeamLite | null>(
    activity.teams[0]
      ? { id: activity.teams[0].id, name: activity.teams[0].name, color: activity.teams[0].color ?? '' }
      : activity.team_ids[0]
        ? { id: activity.team_ids[0], name: '', color: '' }
        : null,
  );
  const teamId = team?.id ?? '';
  const [publicVisibility, setPublicVisibility] = useState<boolean>(activity.public_visibility);
  const [callingLead, setCallingLead] = useState<string>(
    leadToSelect(activity.calling_lead_days ?? null),
  );

  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const qc = useQueryClient();

  // Match details — loaded for `match` activities so the game UI can be
  // prefilled and re-saved. GetFixture returns an empty fixture (not 404) for
  // an activity that isn't a fixture yet, so switching a plain activity to
  // `match` still seeds a blank editor.
  const isMatch = activityType === 'match';
  const fixtureQuery = useQuery({
    queryKey: ['activity-fixture', slug, activity.id],
    queryFn: () => getFixture(token, slug, activity.id),
    enabled: !!token && !!slug && isMatch,
  });
  const [match, setMatch] = useState<MatchDetailsValue>(emptyMatchDetails);
  const matchSeeded = useRef(false);
  useEffect(() => {
    if (!matchSeeded.current && fixtureQuery.data) {
      setMatch(matchDetailsFromFixture(fixtureQuery.data));
      matchSeeded.current = true;
    }
  }, [fixtureQuery.data]);
  const { data: locationsRes } = useQuery({
    queryKey: ['locations', slug, ''],
    queryFn: () => listLocations(token, slug),
    enabled: !!token && !!slug,
  });
  const locationsList = locationsRes?.locations ?? [];
  const selectedLocation = locationsList.find((l) => l.id === locationId);
  const zonesForLocation = useMemo(
    () => selectedLocation?.zones ?? [],
    [selectedLocation],
  );

  useEffect(() => {
    if (
      locationId &&
      selectedLocation &&
      !zonesForLocation.some((z) => z.id === zoneId)
    ) {
      setZoneId('');
    }
  }, [locationId, zoneId, zonesForLocation, selectedLocation]);

  // Resolve "category default" for the calling lead-time picker so the
  // user can see what "inherit" actually evaluates to. Two cached queries:
  // the team (for its category_id + name) and the category list (for the
  // calling_lead_days value). Both are per-org and small — React Query
  // dedupes across other sheets / pages that already use them.
  const teamDetailQuery = useQuery({
    queryKey: ['team', slug, teamId],
    queryFn: () => getTeam(token, slug, teamId),
    enabled: !!token && !!slug && !!teamId,
  });
  const categoriesQuery = useQuery({
    queryKey: ['team-categories', slug],
    queryFn: () => listTeamCategories(token, slug),
    enabled: !!token && !!slug,
  });
  const categoryDefault = useMemo(() => {
    const team = teamDetailQuery.data;
    if (!team?.category_id) return null;
    const category = (categoriesQuery.data?.categories ?? []).find(
      (c) => c.id === team.category_id,
    );
    if (!category) return null;
    return { name: category.name, leadDays: category.calling_lead_days };
  }, [teamDetailQuery.data, categoriesQuery.data]);

  const validationError = (() => {
    if (!title.trim()) return t('schedule.validation.titleRequired');
    // The pickers can't produce a half-empty value, but joinLocal returns ''
    // if one ever did — catch it here so submit never calls toISOString() on
    // an Invalid Date.
    const s = new Date(startsAt).getTime();
    const e = new Date(endsAt).getTime();
    if (Number.isNaN(s) || Number.isNaN(e) || e <= s) {
      return t('schedule.validation.timeOrdering');
    }
    if (!teamId) return t('schedule.validation.teamRequired');
    return null;
  })();

  // Moving the start drags the end along by the same delta so the duration
  // survives a reschedule — mirrors ActivityFormModal.
  function handleStartChange(next: string) {
    const duration = new Date(endsAt).getTime() - new Date(startsAt).getTime();
    const nextStart = new Date(next);
    setStartsAt(next);
    // Guard the incomplete/inverted cases (an empty half parses to NaN) —
    // shifting by NaN would wipe the end field.
    if (Number.isNaN(nextStart.getTime()) || Number.isNaN(duration) || duration <= 0) return;
    setEndsAt(toLocalInput(new Date(nextStart.getTime() + duration).toISOString()));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (validationError) return;
    await onSubmit(
      activity.id,
      {
        title: title.trim(),
        description: description.trim(),
        activity_type: activityType,
        location_id: locationId || null,
        zone_id: zoneId || null,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
        team_ids: [teamId],
        public_visibility: publicVisibility,
        calling_lead_days: leadFromSelect(callingLead, true),
      },
      isMatch ? match : null,
    );
    if (isMatch) {
      void qc.invalidateQueries({ queryKey: ['activity-fixture', slug, activity.id] });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
      <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-4 pt-12 sm:p-6 sm:pt-12">
        <div className="grid grid-cols-[1fr_10rem] gap-3">
          <label className="block">
            <span className="font-heading text-sm font-semibold">
              {t('schedule.activity.title')}
            </span>
            <Input
              required
              className="mt-1"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="font-heading text-sm font-semibold">
              {t('schedule.activity.type.label')}
            </span>
            <Select
              value={activityType}
              onValueChange={(v) => setActivityType(v as ActivityType)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent container={portalContainer}>
                {ACTIVITY_TYPES.map((k) => (
                  <SelectItem key={k} value={k}>
                    {t(`schedule.activity.type.option.${k}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </div>

        {/* Date + time as a click-to-pick pair — same pattern as
            ActivityFormModal and the rest of the platform. A raw
            <input type="datetime-local"> renders mm/dd/yyyy and AM/PM for
            en-US browsers; see the 24h / ISO rules in CLAUDE.md. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <span className="font-heading text-sm font-semibold">
              {t('schedule.activity.startsAt')}
            </span>
            <div className="mt-1 flex items-start gap-2">
              <DatePickerPopover
                className="min-w-0 flex-1"
                value={startDate}
                onChange={(d) => handleStartChange(joinLocal(d, startTime))}
                locale={i18n.language}
                container={portalContainer}
              />
              <TimePickerPopover
                className="w-28 shrink-0"
                value={startTime}
                onChange={(tm) => handleStartChange(joinLocal(startDate, tm))}
                ariaLabel={t('schedule.activity.startsAt')}
              />
            </div>
          </div>
          <div>
            <span className="font-heading text-sm font-semibold">
              {t('schedule.activity.endsAt')}
            </span>
            <div className="mt-1 flex items-start gap-2">
              <DatePickerPopover
                className="min-w-0 flex-1"
                value={endDate}
                onChange={(d) => setEndsAt(joinLocal(d, endTime))}
                locale={i18n.language}
                min={startDate}
                container={portalContainer}
              />
              <TimePickerPopover
                className="w-28 shrink-0"
                value={endTime}
                onChange={(tm) => setEndsAt(joinLocal(endDate, tm))}
                ariaLabel={t('schedule.activity.endsAt')}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="font-heading text-sm font-semibold">
              {t('schedule.activity.location')}
            </span>
            <Select
              value={locationId === '' ? '__none__' : locationId}
              onValueChange={(v) => setLocationId(v === '__none__' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent container={portalContainer}>
                <SelectItem value="__none__">{t('schedule.activity.noLocation')}</SelectItem>
                {locationsList.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-heading text-sm font-semibold">
              {t('schedule.activity.zone')}
            </span>
            <Select
              value={zoneId === '' ? '__none__' : zoneId}
              onValueChange={(v) => setZoneId(v === '__none__' ? '' : v)}
              disabled={!locationId || zonesForLocation.length === 0}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent container={portalContainer}>
                <SelectItem value="__none__">{t('schedule.activity.noZone')}</SelectItem>
                {zonesForLocation.map((z) => (
                  <SelectItem key={z.id} value={z.id}>
                    {z.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </div>

        <div>
          <span className="font-heading text-sm font-semibold">
            {t('templates.slot.team')}
          </span>
          <TeamPicker
            slug={slug}
            value={team}
            onChange={setTeam}
            placeholder={t('templates.slot.teamPlaceholder')}
            searchPlaceholder={t('templates.slot.teamSearchPlaceholder')}
            emptyLabel={t('templates.slot.teamEmpty')}
          />
        </div>

        {isMatch ? (
          <>
            <MatchDetailsSection slug={slug} value={match} onChange={setMatch} />
            {fixtureQuery.data && fixtureQuery.data.opponent_name ? (
              <MatchResultSection
                slug={slug}
                activityId={activity.id}
                fixture={fixtureQuery.data}
                onRecorded={() =>
                  void qc.invalidateQueries({
                    queryKey: ['activity-fixture', slug, activity.id],
                  })
                }
              />
            ) : null}
          </>
        ) : null}

        <label className="block">
          <span className="font-heading text-sm font-semibold">
            {t('schedule.activity.description')}
          </span>
          <textarea
            className="mt-1 w-full rounded-xl border border-border bg-background p-3 text-sm focus:border-primary focus:outline-none"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <label className="flex items-start gap-3 rounded-xl border border-border bg-background p-3">
          <Checkbox
            className="mt-1"
            checked={publicVisibility}
            onChange={(e) => setPublicVisibility(e.target.checked)}
          />
          <span className="text-sm">
            <strong className="font-heading font-semibold">
              {t('schedule.activity.publicVisibility.label')}
            </strong>
            <p className="text-foreground/70">
              {t('schedule.activity.publicVisibility.help')}
            </p>
          </span>
        </label>

        <label className="block">
          <span className="font-heading text-sm font-semibold">
            {t('callings.sendToRoster.label')}
          </span>
          <Select value={callingLead} onValueChange={setCallingLead}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent container={portalContainer}>
              <SelectItem value="inherit">{t('callings.lead.inherit')}</SelectItem>
              {CALLING_LEAD_VALUES.map((v) => (
                <SelectItem key={v} value={leadToSelect(v)}>
                  {t(leadOptionKey(v))}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {categoryDefault ? (
            <p className="mt-1 text-sm text-foreground/70">
              {t('callings.lead.categoryDefault', {
                category: categoryDefault.name,
                value: t(leadOptionKey(categoryDefault.leadDays)),
              })}
            </p>
          ) : (
            <p className="mt-1 text-sm text-foreground/70">
              {t('callings.sendToRoster.help')}
            </p>
          )}
        </label>

        <CallingAccordion slug={slug} activityId={activity.id} activityTitle={activity.title} />

        {(validationError || submitError) ? (
          <p
            role="alert"
            className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {submitError ?? validationError}
          </p>
        ) : null}
      </div>

      <footer className="flex items-center justify-between gap-2 border-t border-border bg-background px-4 py-3 sm:px-6 sm:py-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => void onDelete(activity.id)}
          disabled={submitting || deleting}
          className="text-destructive"
        >
          <Trash weight="bold" />
          {deleting ? t('locations.form.saving') : t('common.delete')}
        </Button>
        <Button type="submit" disabled={submitting || deleting || !!validationError}>
          {submitting ? t('locations.form.saving') : t('locations.form.save')}
        </Button>
      </footer>
    </form>
  );
}

interface CallingAccordionProps {
  slug: string;
  activityId: string;
  activityTitle: string;
}

function CallingAccordion({ slug, activityId, activityTitle }: CallingAccordionProps) {
  const { t, i18n } = useTranslation('common');
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [openValue, setOpenValue] = useState<string>('');

  const callingQuery = useQuery({
    queryKey: ['activity-calling', slug, activityId],
    queryFn: () => listInvitationsForActivity(token, slug, activityId),
    enabled: !!slug && !!activityId,
  });
  const callingData = callingQuery.data;
  const calling = callingData?.calling ?? null;
  const invitations = callingData?.invitations ?? [];
  const callingOpen = calling?.status === 'open';
  const counts = summarise(invitations);
  // Opt-out model: the currently-blocked set, used to toggle individual members.
  const excludedIds = invitations.filter((i) => i.status === 'excluded').map((i) => i.member_id);

  const dateTimeFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
    [i18n.language],
  );

  const createMut = useMutation({
    mutationFn: () =>
      // Opt-out model: no invite list — the whole roster is called on create.
      createCalling(token, slug, {
        activity_id: activityId,
        title: activityTitle,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['activity-calling', slug, activityId] });
      toast.success(t('callings.toast.created'));
    },
    onError: () => toast.error(t('callings.toast.createFailed')),
  });
  // Block / unblock a single member by replacing the full excluded set.
  const excludeMut = useMutation({
    mutationFn: (vars: { memberID: string; block: boolean }) => {
      const next = vars.block
        ? Array.from(new Set([...excludedIds, vars.memberID]))
        : excludedIds.filter((id) => id !== vars.memberID);
      return setCallingExclusions(token, slug, calling!.id, next);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['activity-calling', slug, activityId] });
    },
    onError: () => toast.error(t('callings.toast.excludeFailed')),
  });
  const respondMut = useMutation({
    mutationFn: (vars: { memberID: string; status: 'attending' | 'declined' | 'maybe' }) =>
      respondCalling(token, slug, calling!.id, { member_id: vars.memberID, status: vars.status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['activity-calling', slug, activityId] });
    },
    onError: () => toast.error(t('callings.toast.respondFailed')),
  });
  const closeMut = useMutation({
    mutationFn: () => closeCalling(token, slug, calling!.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['activity-calling', slug, activityId] });
      toast.success(t('callings.toast.closed'));
    },
    onError: () => toast.error(t('callings.toast.closeFailed')),
  });

  async function onCloseCalling() {
    if (
      await confirm({
        title: t('callings.close.confirmTitle'),
        body: t('callings.close.confirmBody'),
        confirmLabel: t('callings.close.cta'),
      })
    ) {
      closeMut.mutate();
    }
  }

  return (
    <Accordion
      type="single"
      collapsible
      value={openValue}
      onValueChange={setOpenValue}
      className="rounded-2xl border border-border bg-card shadow-sm"
    >
      <AccordionItem value="callings" className="border-b-0">
        <AccordionTrigger className="px-4 py-3 sm:px-6">
          <div className="flex flex-1 items-center gap-3">
            <span
              aria-hidden
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/80"
            >
              <UsersThree className="h-5 w-5" weight="bold" />
            </span>
            <div className="flex flex-1 flex-col gap-0.5 text-left">
              <span className="font-heading text-sm font-bold">
                {t('callings.section.title')}
              </span>
              {calling ? (
                <CountsLine t={t} counts={counts} />
              ) : (
                <span className="text-xs text-muted-foreground">
                  {callingData?.scheduled_send_at
                    ? t('callings.section.scheduledFor', {
                        date: dateTimeFmt.format(new Date(callingData.scheduled_send_at)),
                      })
                    : t('callings.section.none')}
                </span>
              )}
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4 sm:px-6 sm:pb-6">
          {!calling ? (
            <Button
              type="button"
              onClick={() => createMut.mutate()}
              disabled={createMut.isPending}
            >
              {createMut.isPending ? t('common.loading') : t('callings.section.create')}
            </Button>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
                <Badge variant={callingOpen ? 'default' : 'secondary'}>
                  {t(`callings.status.${calling.status}`)}
                </Badge>
                {callingOpen ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="ml-auto"
                    onClick={onCloseCalling}
                    disabled={closeMut.isPending}
                  >
                    {t('callings.close.cta')}
                  </Button>
                ) : null}
              </div>

              {calling.message ? (
                <p className="rounded-xl bg-muted/40 px-3 py-2 text-sm text-foreground/90">
                  {calling.message}
                </p>
              ) : null}

              <ul className="flex max-h-[28rem] flex-col divide-y divide-border overflow-y-auto">
                {invitations.map((inv) => {
                  const blocked = inv.status === 'excluded';
                  return (
                    <li
                      key={inv.member_id}
                      className={`flex flex-col gap-2 py-2.5 sm:flex-row sm:items-center sm:justify-between ${
                        blocked ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-heading text-sm font-semibold">{inv.name}</span>
                        <StatusChip status={inv.status} t={t} />
                      </div>
                      {callingOpen ? (
                        blocked ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="shrink-0"
                            onClick={() => excludeMut.mutate({ memberID: inv.member_id, block: false })}
                            disabled={excludeMut.isPending}
                          >
                            {t('callings.exclude.unblock')}
                          </Button>
                        ) : (
                          <div className="flex shrink-0 items-center gap-1.5">
                            <RsvpButton
                              active={inv.status === 'attending'}
                              label={t('callings.respond.yes')}
                              onClick={() => respondMut.mutate({ memberID: inv.member_id, status: 'attending' })}
                              disabled={respondMut.isPending}
                            />
                            <RsvpButton
                              active={inv.status === 'maybe'}
                              label={t('callings.respond.maybe')}
                              onClick={() => respondMut.mutate({ memberID: inv.member_id, status: 'maybe' })}
                              disabled={respondMut.isPending}
                            />
                            <RsvpButton
                              active={inv.status === 'declined'}
                              label={t('callings.respond.no')}
                              onClick={() => respondMut.mutate({ memberID: inv.member_id, status: 'declined' })}
                              disabled={respondMut.isPending}
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="shrink-0 text-muted-foreground"
                              onClick={() => excludeMut.mutate({ memberID: inv.member_id, block: true })}
                              disabled={excludeMut.isPending}
                            >
                              {t('callings.exclude.block')}
                            </Button>
                          </div>
                        )
                      ) : null}
                    </li>
                  );
                })}
                {invitations.length === 0 ? (
                  <li className="py-3 text-sm text-muted-foreground">
                    {t('callings.section.noInvitees')}
                  </li>
                ) : null}
              </ul>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function CountsLine({
  t,
  counts,
}: {
  t: TFunction<'common'>;
  counts: { attending: number; maybe: number; declined: number; invited: number };
}) {
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
      <span>
        {t('callings.counts.attending')}:{' '}
        <strong className="text-foreground">{counts.attending}</strong>
      </span>
      <span>
        {t('callings.counts.maybe')}:{' '}
        <strong className="text-foreground">{counts.maybe}</strong>
      </span>
      <span>
        {t('callings.counts.declined')}:{' '}
        <strong className="text-foreground">{counts.declined}</strong>
      </span>
      <span>
        {t('callings.counts.invited')}:{' '}
        <strong className="text-foreground">{counts.invited}</strong>
      </span>
    </span>
  );
}

function RsvpButton({
  active,
  label,
  onClick,
  disabled,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? 'default' : 'outline'}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </Button>
  );
}

function StatusChip({
  status,
  t,
}: {
  status: CallingResponseStatus;
  t: TFunction<'common'>;
}) {
  const variant =
    status === 'attending'
      ? 'default'
      : status === 'declined'
        ? 'destructive'
        : status === 'maybe'
          ? 'secondary'
          : 'outline';
  return <Badge variant={variant}>{t(`callings.status.${status}`)}</Badge>;
}

function summarise(invitations: ActivityInvitation[]) {
  const counts = { attending: 0, maybe: 0, declined: 0, invited: 0 };
  for (const inv of invitations) {
    if (inv.status in counts) counts[inv.status as keyof typeof counts] += 1;
  }
  return counts;
}

// splitLocal / joinLocal bridge the combined local 'YYYY-MM-DDTHH:MM' form
// state and the separate ISO-date / 24h-time values the two pickers speak.
function splitLocal(v: string): { date: string; time: string } {
  const [date = '', rest = ''] = v.split('T');
  return { date, time: rest.slice(0, 5) };
}

function joinLocal(date: string, time: string): string {
  return date && time ? `${date}T${time}` : '';
}

// toLocalInput converts an ISO 8601 string to the canonical local
// YYYY-MM-DDTHH:MM form the editor holds, in the browser's local TZ (so
// "starts at 18:00" stays at 18:00 wherever the user is).
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return [
    d.getFullYear(),
    '-',
    pad(d.getMonth() + 1),
    '-',
    pad(d.getDate()),
    'T',
    pad(d.getHours()),
    ':',
    pad(d.getMinutes()),
  ].join('');
}
